import {
  compareHuge,
  huge,
  hugeZero,
  type HugeNumber,
  type HugeNumberInput,
} from "@/shared/numbers";
import type { ExplorationTimAccelerationDefinition } from "./explorationTypes";

export interface ExplorationTimAccelerationPlan {
  readonly acceleratedGameElapsedMs: number;
  readonly normalGameElapsedMs: number;
  readonly timConsumed: HugeNumber;
}

export function resolveExplorationTimAccelerationPlan(input: {
  readonly gameElapsedMs: number;
  readonly availableTim: HugeNumberInput;
  readonly hasParticipation: boolean;
  readonly unlocked: boolean;
  readonly enabled: boolean;
  readonly definition: ExplorationTimAccelerationDefinition;
}): ExplorationTimAccelerationPlan {
  assertFiniteNonNegative(input.gameElapsedMs, "gameElapsedMs");
  const availableTim = huge(input.availableTim);
  if (
    input.gameElapsedMs === 0 ||
    !input.hasParticipation ||
    !input.unlocked ||
    !input.enabled ||
    compareHuge(availableTim, 0) <= 0
  ) {
    return noAcceleration(input.gameElapsedMs);
  }

  const fullCost = huge(input.definition.timPerGameSecond).mul(
    input.gameElapsedMs / 1_000,
  );
  if (compareHuge(availableTim, fullCost) >= 0) {
    return {
      acceleratedGameElapsedMs: input.gameElapsedMs,
      normalGameElapsedMs: 0,
      timConsumed: fullCost,
    };
  }

  const acceleratedGameElapsedMs = Math.min(
    input.gameElapsedMs,
    Math.max(
      0,
      availableTim.div(input.definition.timPerGameSecond).toNumber() * 1_000,
    ),
  );
  return {
    acceleratedGameElapsedMs,
    normalGameElapsedMs: input.gameElapsedMs - acceleratedGameElapsedMs,
    timConsumed: availableTim,
  };
}

function noAcceleration(gameElapsedMs: number): ExplorationTimAccelerationPlan {
  return {
    acceleratedGameElapsedMs: 0,
    normalGameElapsedMs: gameElapsedMs,
    timConsumed: hugeZero(),
  };
}

function assertFiniteNonNegative(value: number, name: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be finite and non-negative.`);
  }
}
